import moment from "moment";
import { useParams } from "react-router-dom";
import PropTypes from "prop-types";
import { ReactComponent as CheckIcon } from "assets/images/icons/Check.svg";
import { ReactComponent as PointIcon } from "assets/images/icons/Point.svg";
import { FormattedMessage } from "react-intl";
import { useGetShipmentBasic } from "api/queries/shipments/useGetShipmentBasic";

interface StopData {
  expected_stop_date: string | null;
  expected_stop_time: string;
  updated_stop_date: string;
  updated_stop_time: string;
  label: string;
  vessel_name?: string;
  vessel_number?: string;
  type?: string;
  flight_number?: string;
  port_name?: string;
  port_locode?: string;
  airport?: string;
}

interface StopProps {
  stopData: StopData;
  isLastStop: boolean;
  isDelivered: boolean;
}

const formatDate = (date: string | null): string => {
  if (date === null) return "";
  return moment(date).format("DD MMM");
};

const formatTime = (time: string | null): string => {
  if (time === null) return "";
  return moment(time, "HH:mm:ss").format(" HH:mm");
};

function Stop(props: StopProps) {
  const { stopData, isLastStop, isDelivered } = props;
  const {
    expected_stop_date,
    expected_stop_time,
    updated_stop_date,
    updated_stop_time,
    label,
    vessel_name,
    vessel_number,
    type,
    flight_number,
    port_name,
    port_locode,
    airport,
  }: StopData = stopData;

  const { id } = useParams();
  const { data: shipmentData }: any = useGetShipmentBasic(id!, {
    enabled: !!id,
    refetchOnMount: false,
  });
  const isOffPlatform = shipmentData?.shipment?.is_off_platform_shipment || false;

  // format the dates and times, use these consts to render the datetime text
  const formattedExpectedDate = formatDate(expected_stop_date);
  const formattedExpectedTime = formatTime(expected_stop_time);
  const formattedUpdatedDate = formatDate(updated_stop_date);
  const formattedUpdatedTime = formatTime(updated_stop_time);

  // check if this is a vessel or flight stop
  const isVesselStop = vessel_name || vessel_number || port_name;
  const isFlightStop = flight_number;

  // check if the datetime for this stop has been updated by the provider
  const hasDateTimeBeenUpdated =
    (updated_stop_date || updated_stop_time) &&
    (updated_stop_date !== expected_stop_date || updated_stop_time !== expected_stop_time);

  // check if the updated date is earlier than the expected date
  const updatedDateTime = moment(`${updated_stop_date}T${updated_stop_time}`);
  const expectedDateTime = moment(`${expected_stop_date}T${expected_stop_time}`);
  const isUpdatedDateTimeEarlierThanExpected = updatedDateTime.isBefore(expectedDateTime);

  // check if the stop is complete, we can use this isStopComplete to render the icon later on
  const isStopDateInThePast = hasDateTimeBeenUpdated
    ? moment().isAfter(updatedDateTime)
    : moment().isAfter(expectedDateTime);

  const isStopComplete =
    (isStopDateInThePast &&
      type !== "delivery" &&
      ((hasDateTimeBeenUpdated && updated_stop_date) || (!hasDateTimeBeenUpdated && expected_stop_date))) ||
    isDelivered;

  /**
   * Get the text color for the datetime text
   *    - green if the updated date is earlier than the expected date
   *    - orange if the updated date is later than the expected date
   *    - black if the date is updated but the expected date is unknown
   * @returns {string} - the text color for the datetime text
   */
  const getTextColor = () => {
    if (!expected_stop_date) return "";
    if (isUpdatedDateTimeEarlierThanExpected) return "text-success";
    return "text-warning";
  };

  /**
   * Render the label for the stop
   *   - if this is a vessel stop, render the port name
   *   - if this is a flight stop, render the flight number
   *    - otherwise, render the label from the API
   * @returns {string} - the label for the stop
   */
  const renderLabel = (): string => {
    if (isVesselStop) {
      const prefix = type === "departure_seaport" ? "Departure " : "Arrival ";
      return `${prefix} ${port_locode || port_name}`;
    }
    if (isFlightStop) {
      const prefix = type === "flight_departure" ? "Departure " : "Arrival ";
      return `${prefix} ${label}`;
    }
    return label;
  };

  return (
    <div>
      <div className="d-flex position-relative">
        {/* render the line for the stop */}
        <div
          style={{
            backgroundColor: "white",
            width: 20,
            height: isLastStop ? 40 : 26,
            position: "absolute",
            top: -5,
            left: -1,
            bottom: 0,
          }}
        />
        {/* render the icon for the stop */}
        <div>
          {isStopComplete ? (
            <CheckIcon height={18} width={18} style={{ transform: "translateY(-3px)" }} />
          ) : (
            <PointIcon height={18} width={18} style={{ transform: "translateY(-3px)" }} />
          )}
        </div>
        {/* render the left column; first row is the label, second row is flight+vessel info */}
        <div className="font-b9 ml-3">
          <div>
            <p className="font-b4">{renderLabel()}</p>
          </div>
          <div>
            {isVesselStop && vessel_number && <p className="font-b3 text-secondary">Vessel IMO - {vessel_number}</p>}
            {isFlightStop && flight_number && <p className="font-b3 text-secondary">Flight - {flight_number}</p>}
            {airport && <p className="font-b3 text-secondary">{airport}</p>}
          </div>
        </div>
        {/* render the right column; first row is the date the stop will be completed, 
            second row appears if the date has been updated and we have access to the original expected date. */}
        <div className="ml-auto">
          <div>
            {!isOffPlatform ? (
              <>
                {!hasDateTimeBeenUpdated && (
                  <p className="font-b4 text-right">
                    {formattedExpectedDate}
                    {!isVesselStop && formattedExpectedTime}
                  </p>
                )}
                {hasDateTimeBeenUpdated && (
                  <p className={`font-b4 ${getTextColor()} text-right`}>
                    {formattedUpdatedDate}
                    {!isVesselStop && formattedUpdatedTime}
                  </p>
                )}
              </>
            ) : (
              <p className="font-b3 m-0 text-secondary text-right">N/A</p>
            )}
            {!expected_stop_date && !updated_stop_date && <p className="font-b3 m-0 text-secondary">N/A</p>}
          </div>
          <div>
            {hasDateTimeBeenUpdated && (
              <div className="d-flex justify-content-between mt-1 ml-3">
                <p className="font-b3 text-secondary ml-auto">
                  {formattedExpectedDate && !isOffPlatform && (
                    <span>
                      <FormattedMessage id="general.quoted" defaultMessage="Quoted" /> {formattedExpectedDate}{" "}
                      {!isVesselStop && formattedExpectedTime}
                    </span>
                  )}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
      {!isLastStop && <hr className="separator" />}
    </div>
  );
}

Stop.propTypes = {
  stopData: PropTypes.shape({
    expected_stop_date: PropTypes.string,
    expected_stop_time: PropTypes.string,
    updated_stop_date: PropTypes.string,
    updated_stop_time: PropTypes.string,
    label: PropTypes.string.isRequired,
    vessel_name: PropTypes.string,
    vessel_number: PropTypes.string,
    type: PropTypes.string,
    flight_number: PropTypes.string,
    port_name: PropTypes.string,
    port_locode: PropTypes.string,
    airport: PropTypes.string,
  }).isRequired,
  isLastStop: PropTypes.bool.isRequired,
  isDelivered: PropTypes.bool.isRequired,
};

export default Stop;
